// ===================
// MEDIUM AI BIDDING
// ===================

import type { Card, Artist, GameState } from '../../../types/game'
import type { AuctionState } from '../../../types/auction'
import type { AIDecisionContext } from '../../types'
import type { VisibleGameState } from '../../types/knowledge'
import { PureMathEVCalculator } from './pure-math-ev'
import { MediumAIMarketAnalysis } from './market-analysis'
import { createProbabilityUtils } from '../../utils'

/**
 * Medium AI bidding logic - uses pure math EV calculations
 */
export class MediumAIBidding {
  private evCalculator: PureMathEVCalculator
  private marketAnalysis: MediumAIMarketAnalysis
  private probability = createProbabilityUtils()

  private decisionCount = 0
  private totalConfidence = 0

  constructor(seed?: number) {
    this.evCalculator = new PureMathEVCalculator()
    this.marketAnalysis = new MediumAIMarketAnalysis()
  }

  /**
   * Make strategic open bid
   *
   * Strategy:
   * - Sometimes bluff: bid aggressively (up to 100% of EV) to drive up price
   * - Otherwise: bid conservatively (up to 70% of EV)
   * - Never bid more than 100% of EV
   */
  async makeOpenBid(
    context: AIDecisionContext,
    auction: AuctionState,
    currentHighBid: number | null
  ): Promise<{
    action: 'bid' | 'pass'
    amount?: number
    maxBid?: number
    confidence: number
    reasoning: string
  }> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (!('card' in auction)) {
      return {
        action: 'pass',
        confidence: 0.1,
        reasoning: 'Medium AI: Invalid auction state',
      }
    }

    const card = auction.card

    // Calculate card EV using pure math calculator
    const evResult = this.evCalculator.calculateCardEV(card.artist, gameState, player.hand)
    const cardEV = evResult.thisRound

    // Decide whether to bluff this time (30% chance)
    const isBluffing = Math.random() < 0.3

    // Set maximum bid based on bluff decision
    const maxBidPercent = isBluffing ? 1.0 : 0.90  // 100% if bluffing, 90% otherwise
    const maxBid = Math.floor(cardEV * maxBidPercent)
    const shouldBid = maxBid > (currentHighBid || 0)

    if (!shouldBid || maxBid > player.money) {
      this.decisionCount++
      this.totalConfidence += 0.6

      return {
        action: 'pass',
        confidence: 0.6,
        reasoning: `Medium AI: EV too low (${cardEV.toFixed(1)}) or cannot afford bid`,
      }
    }

    // Calculate bid amount
    let bidAmount: number
    if (currentHighBid === null) {
      // Opening bid
      if (isBluffing) {
        // Bluff: bid 80-100% of EV to show aggression
        bidAmount = Math.floor(cardEV * (0.8 + Math.random() * 0.2))
      } else {
        // Normal: 50-70% of max
        bidAmount = Math.floor(maxBid * (0.5 + Math.random() * 0.2))
      }
    } else {
      // Raise bid: minimum increment
      const minIncrement = Math.max(1, Math.floor(currentHighBid * 0.1))
      bidAmount = currentHighBid + minIncrement
    }

    // Ensure bid doesn't exceed maxBid
    bidAmount = Math.min(bidAmount, maxBid)

    // Validate bid is actually higher than current bid
    if (currentHighBid !== null && bidAmount <= currentHighBid) {
      // Can't afford to raise - pass
      this.decisionCount++
      this.totalConfidence += 0.5

      return {
        action: 'pass',
        confidence: 0.5,
        reasoning: `Medium AI: Max bid (${maxBid}) too low to raise current bid (${currentHighBid})`,
      }
    }

    this.decisionCount++
    const confidence = Math.min(0.9, 0.5 + (evResult.probabilities.first * 0.4))
    this.totalConfidence += confidence

    return {
      action: 'bid',
      amount: Math.max(1, bidAmount),
      maxBid: Math.min(player.money, maxBid),
      confidence,
      reasoning: `Medium AI: Bid ${bidAmount} with EV ${cardEV.toFixed(1)} (P1st: ${evResult.probabilities.first.toFixed(2)})${isBluffing ? ' [bluff]' : ''}`,
    }
  }

  /**
   * Make strategic one-offer bid
   * Uses EV-based bidding strategy:
   * - If no bid: bid 0.6-0.7 of EV (opening bid)
   * - If bid exists: bid higher than current, but < EV, max 0.9 * EV
   */
  async makeOneOfferBid(
    context: AIDecisionContext,
    auction: AuctionState,
    currentBid: number
  ): Promise<{
    action: 'bid' | 'pass'
    amount?: number
    position?: 'first' | 'middle' | 'last' | 'auctioneer'
    confidence: number
    reasoning: string
  }> {
    const { gameState } = context

    // DEBUG: Log context and game state
    console.log('[EV DEBUG] makeOneOfferBid context:', {
      playerIndex: context.playerIndex,
      totalPlayers: gameState.players.length,
      playerIds: gameState.players.map(p => p.id),
      hasMyHand: !!(context as any).myHand,
      myHandLength: (context as any).myHand?.length || 0,
      gameState: 'round=' + gameState.round.roundNumber + ' phase=' + gameState.round.phase.type,
      currentBid // Log current bid for debugging
    })

    // Get hand from context (myHand) instead of getting player from gameState
    // This avoids the playerIndex mismatch issue
    const aiHand = (context as any).myHand || []
    const myMoney = (context as any).myMoney || 0

    if (!('card' in auction)) {
      return {
        action: 'pass',
        confidence: 0.1,
        reasoning: 'Medium AI: Invalid auction state',
      }
    }

    const card = auction.card
    const position = this.calculateBidPosition(gameState, context.playerIndex, auction.auctioneerId)

    // Calculate card EV using pure math calculator
    const evResult = this.evCalculator.calculateCardEV(card.artist, gameState, aiHand)
    const cardEV = evResult.thisRound  // Use this round's EV

    // EV-based bidding strategy
    let bidAmount: number
    let maxBid: number

    // Check if there's an existing bid (currentBid > 0)
    const hasExistingBid = currentBid > 0

    if (!hasExistingBid) {
      // Opening bid: 0.6-0.7 of EV (with some randomness for variety)
      const bidPercentage = 0.6 + Math.random() * 0.1 // 0.6 to 0.7
      bidAmount = Math.floor(cardEV * bidPercentage)
      maxBid = Math.floor(cardEV * 0.7) // Cap opening at 70%

      // Ensure minimum bid of 1
      bidAmount = Math.max(1, bidAmount)

      // Check if we can afford the opening bid
      if (bidAmount > myMoney) {
        this.decisionCount++
        this.totalConfidence += 0.5

        return {
          action: 'pass',
          position,
          confidence: 0.5,
          reasoning: `Medium AI: Cannot afford opening bid (${bidAmount}) with ${myMoney}`,
        }
      }

      this.decisionCount++
      this.totalConfidence += 0.7

      return {
        action: 'bid',
        amount: bidAmount,
        position,
        confidence: 0.7,
        reasoning: `Medium AI: Opening bid ${bidAmount} (EV: ${cardEV.toFixed(1)}, ${Math.floor(bidPercentage * 100)}% of EV)`,
      }
    } else {
      // Raise: bid higher than current, but < EV, max 0.9 * EV
      maxBid = Math.floor(cardEV * 0.9) // Never more than 90% of EV

      // Check if we can even compete
      if (maxBid <= currentBid) {
        this.decisionCount++
        this.totalConfidence += 0.5

        return {
          action: 'pass',
          position,
          confidence: 0.5,
          reasoning: `Medium AI: Max bid (${maxBid}) <= current bid (${currentBid}), EV too low`,
        }
      }

      // Calculate minimum increment
      const minIncrement = Math.max(1, Math.floor(currentBid * 0.1))
      const proposedBid = currentBid + minIncrement

      // Ensure bid doesn't exceed maxBid
      bidAmount = Math.min(proposedBid, maxBid)

      // CRITICAL: Check if bid is actually higher than current bid
      // This can happen when maxBid < currentBid + minIncrement
      if (bidAmount <= currentBid) {
        this.decisionCount++
        this.totalConfidence += 0.5

        return {
          action: 'pass',
          position,
          confidence: 0.5,
          reasoning: `Medium AI: Calculated bid (${bidAmount}) not higher than current bid (${currentBid}), passing`,
        }
      }

      // Check if we can afford this bid
      if (bidAmount > myMoney) {
        this.decisionCount++
        this.totalConfidence += 0.5

        return {
          action: 'pass',
          position,
          confidence: 0.5,
          reasoning: `Medium AI: Cannot afford raise (${bidAmount}) with ${myMoney}`,
        }
      }

      this.decisionCount++
      this.totalConfidence += 0.7

      return {
        action: 'bid',
        amount: bidAmount,
        position,
        confidence: 0.7,
        reasoning: `Medium AI: Raising to ${bidAmount} (current: ${currentBid}, EV: ${cardEV.toFixed(1)}, max: ${maxBid})`,
      }
    }
  }

  /**
   * Make strategic hidden bid
   */
  async makeHiddenBid(
    context: AIDecisionContext,
    auction: AuctionState
  ): Promise<{
    amount: number
    confidence: number
    bluffFactor?: number
    reasoning: string
  }> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (!('card' in auction)) {
      return {
        amount: 0,
        confidence: 0.1,
        reasoning: 'Medium AI: Invalid auction state',
      }
    }

    const card = auction.card
    const evResult = this.evCalculator.calculateCardEV(card.artist, gameState, player.hand)
    const cardEV = evResult.thisRound

    // Hidden bid strategy: bid 70% of EV with variance
    let baseBid = Math.floor(cardEV * 0.7)

    // Add variance for unpredictability (±15%)
    // Resulting bid range: 55% to 85% of EV
    const variance = Math.floor(cardEV * 0.15 * (this.probability.random() - 0.5))
    baseBid = Math.max(0, baseBid + variance)

    const finalBid = Math.min(baseBid, player.money)

    this.decisionCount++
    const confidence = 0.6
    this.totalConfidence += confidence

    return {
      amount: finalBid,
      confidence,
      reasoning: `Medium AI: Hidden bid ${finalBid} (EV: ${cardEV.toFixed(1)}, 70% ± variance)`,
    }
  }

  /**
   * Set strategic fixed price
   */
  async setFixedPrice(
    context: AIDecisionContext,
    auction: AuctionState
  ): Promise<{
    price: number
    confidence: number
    priceReasoning: 'aggressive' | 'conservative' | 'optimal' | 'desperate'
    reasoning: string
  }> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (!('card' in auction)) {
      return {
        price: 10,
        confidence: 0.1,
        priceReasoning: 'conservative',
        reasoning: 'Medium AI: Invalid auction state',
      }
    }

    const card = auction.card
    const evResult = this.evCalculator.calculateCardEV(card.artist, gameState, player.hand)
    const cardEV = evResult.thisRound

    // Fixed price strategy: aim for 80% of EV
    let targetPrice = Math.floor(cardEV * 0.8)

    // Ensure price is reasonable
    targetPrice = Math.max(10, Math.min(targetPrice, player.money * 0.8))

    // Determine pricing strategy
    let priceReasoning: 'aggressive' | 'conservative' | 'optimal' | 'desperate'
    const priceRatio = targetPrice / cardEV

    if (priceRatio > 0.85) {
      priceReasoning = 'aggressive'
    } else if (priceRatio > 0.7) {
      priceReasoning = 'optimal'
    } else if (priceRatio > 0.5) {
      priceReasoning = 'conservative'
    } else {
      priceReasoning = 'desperate'
    }

    this.decisionCount++
    const confidence = 0.8 - Math.abs(priceRatio - 0.8) // Confidence higher near optimal ratio (0.8)
    this.totalConfidence += confidence

    return {
      price: targetPrice,
      confidence,
      priceReasoning,
      reasoning: `Medium AI: Fixed price ${targetPrice} based on EV ${cardEV.toFixed(1)} and competition`,
    }
  }

  /**
   * Make strategic double offer decision
   */
  async makeDoubleOffer(
    context: AIDecisionContext,
    auction: AuctionState
  ): Promise<{
    action: 'offer' | 'decline'
    cardId?: string
    strategy?: 'control_artist' | 'force_auction' | 'conserve_cards' | 'opposition'
    confidence: number
    reasoning: string
  }> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (!('card' in auction)) {
      return {
        action: 'decline',
        confidence: 0.1,
        strategy: 'conserve_cards',
        reasoning: 'Medium AI: Invalid auction state',
      }
    }

    const primaryCard = auction.card
    const matchingCards = player.hand.filter(
      card => card.artist === primaryCard.artist && card.id !== primaryCard.id
    )

    // Analyze artist control opportunity
    const marketAnalysis = this.marketAnalysis.analyzeMarket(gameState, context.playerIndex)
    const artistCompetitiveness = marketAnalysis.artistCompetitiveness[primaryCard.artist]

    // Strategic decision
    let shouldOffer = false
    let strategy: 'control_artist' | 'force_auction' | 'conserve_cards' | 'opposition' = 'conserve_cards'
    let selectedCard: Card | undefined

    // Offer if it helps control artist and we have cards
    if (matchingCards.length > 0 && artistCompetitiveness.cardsNeededForValue <= 2) {
      shouldOffer = true
      strategy = 'control_artist'
      selectedCard = this.probability.randomChoice(matchingCards)
    }
    // Offer to force auction if we have good position
    else if (matchingCards.length > 0) {
      let auctioneerIndex: number
      if ('round' in gameState) {
        auctioneerIndex = (gameState as unknown as GameState).round.currentAuctioneerIndex
      } else {
        auctioneerIndex = (gameState as unknown as VisibleGameState).auctioneerIndex ?? 0
      }
      if (context.playerIndex === auctioneerIndex) {
        shouldOffer = true
        strategy = 'force_auction'
        selectedCard = matchingCards[0]
      }
    }
    // Offer for strategic opposition
    else if (matchingCards.length > 1 && artistCompetitiveness.rank <= 2) {
      shouldOffer = this.probability.random() < 0.4
      if (shouldOffer) {
        strategy = 'opposition'
        selectedCard = matchingCards[0]
      }
    }

    if (!shouldOffer || !selectedCard) {
      this.decisionCount++
      this.totalConfidence += 0.6

      return {
        action: 'decline',
        strategy,
        confidence: 0.6,
        reasoning: `Medium AI: Declining double offer to conserve cards`,
      }
    }

    this.decisionCount++
    this.totalConfidence += 0.7

    return {
      action: 'offer',
      cardId: selectedCard.id,
      strategy,
      confidence: 0.7,
      reasoning: `Medium AI: Offering ${selectedCard.artist} card for ${strategy} strategy`,
    }
  }

  /**
   * Estimate competition level in auction
   */
  private estimateCompetitionLevel(gameState: GameState, playerIndex: number): 'low' | 'medium' | 'high' {
    const player = gameState.players[playerIndex]
    const avgMoney = gameState.players.reduce((sum, p) => sum + p.money, 0) / gameState.players.length

    if (player.money > avgMoney * 1.5) {
      return 'low' // We have money advantage
    } else if (player.money < avgMoney * 0.7) {
      return 'high' // We have money disadvantage
    } else {
      return 'medium'
    }
  }

  /**
   * Calculate bid position in one-offer auction
   */
  private calculateBidPosition(
    gameState: GameState | VisibleGameState,
    playerIndex: number,
    auctioneerId: string
  ): 'first' | 'middle' | 'last' | 'auctioneer' {
    const auctioneerIndex = gameState.players.findIndex(p => p.id === auctioneerId)
    if (auctioneerIndex === playerIndex) {
      return 'auctioneer'
    }

    // Calculate position in bidding order (starts left of auctioneer)
    const playerCount = gameState.players.length
    const positionInOrder = (playerIndex - auctioneerIndex - 1 + playerCount) % playerCount

    if (positionInOrder === 0) {
      return 'first'
    } else if (positionInOrder === playerCount - 2) {
      return 'last'
    } else {
      return 'middle'
    }
  }

  /**
   * Get multiplier based on bidding position
   */
  private getPositionMultiplier(position: 'first' | 'middle' | 'last' | 'auctioneer'): number {
    const multipliers = {
      first: 0.8,     // Bid conservatively when first
      middle: 1.0,    // Normal bidding in middle
      last: 1.2,      // Can be more aggressive when last
      auctioneer: 0.6, // Most conservative as auctioneer
    }

    return multipliers[position]
  }

  /**
   * Initialize with game state
   */
  initialize(gameState: any, playerIndex: number): void {
    this.decisionCount = 0
    this.totalConfidence = 0
  }

  /**
   * Update with new context
   */
  update(context: AIDecisionContext): void {
    // Medium AI adapts based on market changes
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    // No resources to clean up
  }

  /**
   * Get the EV calculator instance
   */
  getEVCalculator(): PureMathEVCalculator {
    return this.evCalculator
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      decisionsMade: this.decisionCount,
      totalConfidence: this.totalConfidence,
    }
  }
}